import { useEffect, useState } from "react";
import InstallPWADialog from "./installpopup";
import { SimpleModalWithBackground } from "../modal";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<{ outcome: "accepted" | "dismissed" }>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const POPUP_TIME = 5; // 10 seconds initial delay
const REOPEN_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds

let deferredPrompt: BeforeInstallPromptEvent | null = null;

function PWAInstallHandler() {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isInstalledPWA, setIsInstalledPWA] = useState<boolean>(false);
  const [isAlreadyShownDialog, setIsAlreadyShownDialog] =
    useState<boolean>(false);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      deferredPrompt = e;
    };

    window.addEventListener("beforeinstallprompt", handler);

    setIsInstalledPWA(window.matchMedia("(display-mode: standalone)").matches);

    const lastDismissed = localStorage.getItem("installDialogDismissed");
    if (lastDismissed) {
      const elapsedTime = Date.now() - parseInt(lastDismissed, 10);
      if (elapsedTime < REOPEN_INTERVAL) {
        setIsAlreadyShownDialog(true);
      }
    }

    setTimeout(() => {
      if (
        !isAlreadyShownDialog &&
        !isInstalledPWA &&
        deferredPrompt &&
        !lastDismissed
      ) {
        setShowDialog(true);
      }
    }, POPUP_TIME * 1000);

    setTimeout(() => {
      if (
        !isAlreadyShownDialog &&
        !isInstalledPWA &&
        deferredPrompt &&
        lastDismissed
      ) {
        setShowDialog(true);
      }
    }, REOPEN_INTERVAL);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, [isAlreadyShownDialog, isInstalledPWA]);

  const installPWA = async () => {
    if (!deferredPrompt) return;

    const { outcome } = await deferredPrompt.prompt();

    if (outcome === "accepted") {
      setShowDialog(false);
      localStorage.removeItem("installDialogDismissed");
    } else {
      closeDialog();
    }

    deferredPrompt = null; // Reset after use
  };

  const closeDialog = () => {
    setShowDialog(false);
    localStorage.setItem("installDialogDismissed", Date.now().toString());
    setTimeout(() => {
      setIsAlreadyShownDialog(false);
    }, REOPEN_INTERVAL);
  };

  if (showDialog) {
    return (
      <SimpleModalWithBackground isOpen={showDialog} handleClose={closeDialog}>
        <InstallPWADialog installPWA={installPWA} closeDialog={closeDialog} />
      </SimpleModalWithBackground>
    );
  }

  return null;
}

export default PWAInstallHandler;
